<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="1150px"
  >
    <!--<editor-fold desc="Dialog Title">-->
    <template slot="dialogTitle">
      <span v-if="yachtDirty.id">
        Edit Yacht
      </span>
      <span v-else>
        Start New Yacht Sales
      </span>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Content">-->
    <v-card-text class="pa-0">
      <v-row
        class="fill-height"
        no-gutters
      >
        <!--<editor-fold desc="Form">-->
        <v-col
          lg="11"
          class="pa-6 fill-height"
          style="overflow-y: auto"
        >
          <v-alert
            v-if="salesSlot"
            outlined
            color="secondary"
            :value="true"
            class="body-1 pa-2"
          >
            New yacht sales for sales slot <span class="font-weight-bold">{{ salesSlot.yard_id }}</span>
            &middot;
            model <strong>{{ salesSlot.yacht_manufacturer.name }} {{ salesSlot.yacht_model }}</strong>
          </v-alert>

          <!--<editor-fold desc="Basic">-->
          <v-subheader>
            Basic
          </v-subheader>
          <v-autocomplete
            v-model="yachtDirty.country_id"
            v-validate="'required'"
            :error-messages="veeErrors.collect('country_id')"
            :items="charteringCountries.data"
            :loading="charteringCountries.loading"
            :disabled="!canEditCountry"
            label="Destination Country"
            item-text="name"
            item-value="id"
            data-vv-as="country"
            data-vv-name="country_id"
            autocomplete="disabled"
            outlined
          />
          <v-autocomplete
            v-model="yachtManufacturerId"
            v-validate="'required'"
            :error-messages="veeErrors.collect('yacht_manufacturer_id')"
            :items="yachtManufacturers.data"
            :loading="yachtManufacturers.loading"
            label="Yacht Manufacturer"
            item-text="name"
            item-value="id"
            data-vv-as="yacht manufacturer"
            data-vv-name="yacht_manufacturer_id"
            autocomplete="off"
            outlined
            :disabled="!canEditYachtModel"
          />

          <yacht-model-select
            v-model="yachtDirty.yacht_model_id"
            v-validate="'required'"
            :error-messages="veeErrors.collect('yacht_model_id')"
            :items="yachtModels.data"
            :loading="yachtModels.loading"
            outlined
            clearable
            data-vv-name="yacht_model_id"
            :disabled="yachtModels.loading || !canEditYachtModel || !yachtDirty.country_id"
            @input="configureDialog = true"
          />

          <v-row
            v-if="selectedYachtModel"
            no-gutters
            align="center"
            class="mb-8"
          >
            <v-col
              cols="auto"
            >
              <v-avatar
                color="grey lighten-3"
                size="55px"
              >
                <v-img
                  v-if="selectedYachtModel.main_image"
                  :src="selectedYachtModel.main_image.path"
                  alt="Main image"
                />
              </v-avatar>
            </v-col>
            <v-col class="px-6">
              <div class="font-weight-bold body-1 text-truncate">
                <a
                  href="#"
                  @click.stop="viewModelDialog = true"
                >{{ selectedYachtModel.full_label }}</a>
              </div>
              <div class="grey--text text--lighten-1 body-2 text-truncate">
                ID {{ selectedYachtModel.id }}
                &middot;
                <strong class="grey--text">
                  {{ selectedYachtModel.cabin_count_description }}
                </strong> cabins
                &middot;
                <strong class="grey--text">
                  {{ selectedYachtModel.berth_count_description }}
                </strong> RNOG
                &middot;
                {{ selectedYachtModel.type.label }}
              </div>
              <div
                class="grey--text text--lighten-1 body-2 text-truncate"
              >
                {{ selectedYachtModel.engines_description || 'Engines not set' }}
              </div>
            </v-col>
            <v-col cols="auto">
              <div class="text-no-wrap">
                <v-btn
                  color="grey darken-2"
                  class="text-none"
                  text
                  @click="viewModelDialog = true"
                >
                  Details
                </v-btn>
                <v-btn
                  color="primary"
                  class="text-none"
                  :disabled="!yachtDirty.yacht_model_id || !canEditEquipmentPacks"
                  @click="configureDialog = true"
                >
                  Configure
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <v-select
            v-model="yachtDirty.production_year"
            v-validate="'required'"
            :error-messages="veeErrors.collect('production_year')"
            :items="deliveryYearSelectItems"
            name="pricelist_year"
            label="Pricelist / Sale / Production Model Year"
            outlined
            data-vv-as="pricelist / sale / production model year"
            hint="Actual model year for the yacht being sold. Results in price increase if different than defined yacht model."
            persistent-hint
          />

          <v-select
            v-model="yachtDirty.currency"
            :items="['EUR', 'GBP', 'USD', 'SEK']"
            label="Select Currency"
            outlined
          />
          <!--</editor-fold>-->

          <!--<editor-fold desc="Investor">-->
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <v-subheader>
                Yacht owner
              </v-subheader>
            </v-col>
            <v-col
              v-if="canEditOwner"
              class="shrink"
            >
              <v-btn
                color="secondary"
                text
                @click="ownerDialogVisible = true"
              >
                <v-icon left>
                  add
                </v-icon>
                Add New
              </v-btn>
            </v-col>
          </v-row>

          <v-autocomplete
            v-model="yachtDirty.owner_id"
            :items="owners.data"
            :loading="owners.loading"
            :disabled="!canEditOwner"
            label="Select yacht owner"
            item-text="name"
            item-value="id"
            autocomplete="off"
            clearable
            outlined
          />
          <!--</editor-fold>-->

          <div v-if="yachtDirty.id || !isSales">
            <v-text-field
              v-if="yachtDirty.id || $can('always-edit-hull-number', 'Yacht')"
              v-model="yachtDirty.hull_number"
              data-vv-as="hull number"
              :error-messages="veeErrors.collect('hull_number')"
              :disabled="!canEditHullNumber"
              name="hull_number"
              label="Hull Number"
              outlined
            />
            <v-text-field
              v-if="yachtDirty.id || $can('always-edit-name', 'Yacht')"
              v-model="yachtDirty.name"
              :error-messages="veeErrors.collect('name')"
              :disabled="!canEditName"
              name="name"
              label="Name"
              data-vv-as="name"
              outlined
            />
            <v-autocomplete
              v-if="yachtDirty.id || $can('always-edit-name', 'Yacht')"
              v-model="yachtDirty.flag_country_id"
              :error-messages="veeErrors.collect('flag_country_id')"
              :items="countries.data"
              :loading="countries.loading"
              :disabled="!canEditFlagCountry"
              label="Flag (Registration) Country"
              item-text="name"
              item-value="id"
              data-vv-as="flag (registration) country"
              data-vv-name="flag_country_id"
              autocomplete="off"
              outlined
            />
            <v-text-field
              v-model="yachtDirty.registration_port"
              data-vv-as="port of registration"
              :error-messages="veeErrors.collect('registration_port')"
              name="registration_port"
              label="Port Of Registration"
              outlined
            />

            <!--<editor-fold desc="Logistics">-->
            <div v-if="$can('set-logistics-plan', 'Yacht')">
              <v-subheader class="mt-4">
                Logistics
              </v-subheader>
              <v-switch
                v-if="!yachtDirty.id"
                v-model="yachtDirty.is_already_ordered"
                label="Yacht is already ordered"
              />
              <v-autocomplete
                v-if="yacht && yacht.status === yachtStatusInUse"
                v-model="yachtDirty.current_base_id"
                :error-messages="veeErrors.collect('current_base_id')"
                :items="basesFilteredByCountry"
                :disabled="!canEditDestinationBase || !yachtDirty.country_id"
                name="current_base_id"
                label="Current Base"
                item-text="name"
                item-value="id"
                outlined
                data-vv-as="current base"
              />
              <v-autocomplete
                v-else
                v-model="yachtDirty.destination_base_id"
                :error-messages="veeErrors.collect('destination_base_id')"
                :items="basesFilteredByCountry"
                :disabled="!canEditDestinationBase || !yachtDirty.country_id"
                name="destination_base_id"
                label="Destination Base"
                item-text="name"
                item-value="id"
                outlined
                data-vv-as="destination base"
              />
              <!--              <v-select-->
              <!--                v-model="yachtDirty.delivery_year"-->
              <!--                :error-messages="veeErrors.collect('delivery_year')"-->
              <!--                :items="deliveryYearSelectItems"-->
              <!--                :disabled="!canEditDeliveryYear"-->
              <!--                name="delivery_year"-->
              <!--                label="Delivery Year"-->
              <!--                outlined-->
              <!--                data-vv-as="delivery year"-->
              <!--              />-->
              <v-dialog
                ref="datePickerVisible"
                v-model="datePickerVisible"
                :fullscreen="$vuetify.breakpoint.smAndDown"
                transition="fade-transition"
                width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :error-messages="veeErrors.collect('ex_factory_date')"
                    :disabled="!canEditExFactoryDate"
                    :value="yachtDirty.ex_factory_date ? $moment(yachtDirty.ex_factory_date).format('DD.MM.YYYY.') : ''"
                    hide-details
                    name="ex_factory_date"
                    label="Ex Factory Date"
                    readonly
                    outlined
                    clearable
                    data-vv-as="ex factory date"
                    @click:clear="yachtDirty.ex_factory_date = null"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="yachtDirty.ex_factory_date"
                  no-title
                  full-width
                  scrollable
                  @input="datePickerVisible = false"
                />
              </v-dialog>
            </div>
            <!--</editor-fold>-->
          </div>
        </v-col>
        <!--</editor-fold>-->

        <!--<editor-fold desc="Price Column">-->
        <v-col
          v-if="$can('view-sale-price', 'Yacht')"
          lg="5"
          class="pa-6 fill-height"
          style="overflow-y: auto"
        >
          <v-subheader>
            Price
          </v-subheader>

          <small-pricing-list
            v-if="selectedYachtModel && hasReconfigured"
            class="mb-6"
            live-calculation
            :equipment-packs="selectedEquipmentPacksDirty"
            :base-price="selectedYachtModel.sale_price"
            :base-currency="selectedYachtModel.currency"
            :currency="yachtDirty.currency"
            :currency-exchange-year="yachtDirty.production_year"
            :model-year="selectedYachtModel.model_year"
            :production-year="yachtDirty.production_year"
            :yearly-price-increase="Number(selectedYachtModel.manufacturer.yearly_price_increase)"
          />

          <small-pricing-list
            v-else-if="yacht.id"
            class="mb-6"
            :live-calculation="hasChangedCurrencyOrProductionYear"
            :equipment-packs="selectedEquipmentPacksDirty"
            :base-price="hasChangedCurrencyOrProductionYear ? yacht.model.sale_price : yacht.base_sale_price"
            :equipment-price="yacht.equipment_sale_price"
            :base-currency="yacht.model.currency"
            :currency="yachtDirty.currency"
            :currency-exchange-year="yachtDirty.production_year"
            :cdr-cost="yacht.cdr_price"
            :total-price="yacht.total_sale_price"
            :model-year="yacht.model.model_year"
            :production-year="yachtDirty.production_year"
            :production-year-price-increase="yacht.production_year_price_increase"
            :yearly-price-increase="Number(yacht.model.manufacturer.yearly_price_increase)"
            :exchange-rates="savedExchangeRates"
          />
        </v-col>
        <!--</editor-fold>-->
      </v-row>
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <!-- TODO: find a way to not show the button if ability not granted -->
      <v-btn
        v-if="yachtDirty.id"
        small
        outlined
        color="error"
        @click="toggleConfirmationDialog(true)"
      >
        <v-icon
          left
          small
        >
          delete
        </v-icon>
        Delete
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :loading="submitting"
        :disabled="selectedEquipmentPacksDirty.length === 0 && !yacht.id"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialogs">-->
    <yacht-model-configure-equipment-dialog
      v-if="yachtDirty.yacht_model_id && canEditEquipmentPacks"
      :visible.sync="configureDialog"
      :country-id="yachtDirty.country_id"
      :yacht-model-id="yachtDirty.yacht_model_id"
      :selected-packs="selectedEquipmentPacksDirty"
      :selected-currency="yachtDirty.currency"
      :selected-production-year="yachtDirty.production_year"
      :updates-pullable="!(yachtDirty.id && yachtDirty.investment_status === yachtStatusSold)"
      @configuration="configurePacks"
    />

    <yacht-model-view-dialog
      v-if="yachtDirty.yacht_model_id"
      :yacht-model-id="yachtDirty.yacht_model_id"
      :country-id="yachtDirty.country_id"
      :visible.sync="viewModelDialog"
      :has-actions="false"
    />

    <owner-edit-dialog
      :visible.sync="ownerDialogVisible"
      @owner:created="ownerCreated"
    />

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this Yacht?"
      @confirmed="deleteYacht"
    />
    <!--</editor-fold>-->
  </content-dialog>
</template>

<script>
import { cloneDeep, debounce, sortBy } from 'lodash'
import Yacht from '@/models/Yacht'
import YachtModel from '@/models/YachtModel'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ContentDialog from '@/components/helpers/Dialog'
import OwnerEditDialog from '@/components/business/owners/OwnerEditDialog'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import OwnersApi from '@/services/api/Owners'
import { mapGetters, mapState } from 'vuex'
import YachtModelSelect from '@/components/helpers/YachtModelSelect'
import YachtModelConfigureEquipmentDialog from '@/components/business/yachtModels/YachtModelConfigureEquipmentDialog'
import YachtModelViewDialog from '@/components/business/yachtModels/YachtModelViewDialog'
import SmallPricingList from '@/components/business/yachts/SmallPricingList'

export default {
  name: 'YachtEditDialog',
  components: {
    SmallPricingList,
    YachtModelViewDialog,
    YachtModelConfigureEquipmentDialog,
    YachtModelSelect,
    ContentDialog,
    OwnerEditDialog,
    ConfirmDeleteDialog
  },
  mixins: [deleteConfirmationMixin],
  props: {
    visible: Boolean,
    yacht: {
      type: Object,
      default: () => Yacht.emptyModel
    },
    yachtModelId: {
      type: Number,
      default: undefined
    },
    preselectYachtManufacturerId: {
      type: Number,
      default: undefined
    },
    countryId: {
      type: Number,
      default: undefined
    },
    isSales: {
      type: Boolean,
      default: true
    },
    salesSlot: {
      type: Object,
      default: () => undefined
    }
  },
  data() {
    return {
      yachtDirty: cloneDeep(this.yacht),
      yachtManufacturerId: null,
      deliveryYear: null,
      selectedEquipmentPacksDirty: [],
      hasReconfigured: false,
      hasChangedCurrencyOrProductionYear: false,
      submitting: false,
      yachtModels: {
        loading: false,
        data: []
      },
      owners: {
        loading: false,
        data: []
      },
      equipmentPacks: {
        loading: false,
        data: []
      },
      ownerDialogVisible: false,
      datePickerVisible: false,
      configureDialog: false,
      viewModelDialog: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    selectedYachtModel() {
      if (!this.yachtDirty.yacht_model_id || this.yachtModels.loading) return undefined

      return this.yachtModels.data.find(m => m.id === this.yachtDirty.yacht_model_id) || null
    },
    canEditEquipmentPacks() {
      return this.yachtDirty.editable_fields.indexOf('equipment_packs') > -1
    },
    canEditOwner() {
      return this.yachtDirty.editable_fields.indexOf('owner_id') > -1
    },
    canEditYachtModel() {
      return this.yachtDirty.editable_fields.indexOf('yacht_model_id') > -1
    },
    canEditCountry() {
      return this.yachtDirty.editable_fields.indexOf('country_id') > -1
    },
    canEditFlagCountry() {
      return this.yachtDirty.editable_fields.indexOf('flag_country_id') > -1
    },
    canEditName() {
      return this.yachtDirty.editable_fields.indexOf('name') > -1
    },
    canEditHullNumber() {
      return this.yachtDirty.editable_fields.indexOf('hull_number') > -1
    },
    canEditDeliveryYear() {
      return this.yachtDirty.editable_fields.indexOf('delivery_year') > -1
    },
    canEditDestinationBase() {
      return this.yachtDirty.editable_fields.indexOf('destination_base_id') > -1
    },
    canEditExFactoryDate() {
      return this.yachtDirty.editable_fields.indexOf('ex_factory_date') > -1
    },
    canEditPartnerSalesAvailability() {
      return this.yachtDirty.editable_fields.indexOf('partners_visible') > -1
    },
    basesFilteredByCountry() {
      if (!this.yachtDirty.country_id) return []

      return this.$store.getters['business/filterBasesByCountryId'](this.yachtDirty.country_id)
    },
    deliveryYearSelectItems() {
      let items = []
      for (let i = 0; i < 20; i++) {
        items.push(this.currentYear + i)
      }
      return items
    },
    savedExchangeRates() {
      if (!this.yacht.id) return []

      const rates = []

      if (this.yacht.currency_exchange_rate && parseFloat(this.yacht.currency_exchange_rate) !== 1) {
        rates.push({
          base: this.yacht.original_currency,
          quote: this.yacht.currency,
          rate: this.yacht.currency_exchange_rate
        })
      }

      this.yacht.equipment_packs.map(ep => {
        if (!ep.currency_exchange_rate) return

        rates.push({
          base: ep.currency,
          quote: ep.final_currency,
          rate: ep.currency_exchange_rate
        })
      })

      return rates
    },
    ...mapGetters('settings', ['charteringCountries', 'countries', 'yachtManufacturers']),
    ...mapState('settings', ['currencyExchangeRates'])
  },
  watch: {
    'yachtDirty.country_id': function () {
      this.yachtDirty.destination_base_id = this.yacht.destination_base_id || null

      if (!this.yacht.id) {
        this.selectedEquipmentPacksDirty = []
        this.fetchYachtModels()
      }
    },
    'yachtDirty.yacht_model_id': function () {
      this.selectedEquipmentPacksDirty = []
    },
    selectedYachtModel: function (value) {
      this.setYachtEngineSerialNumbersArray()
      this.setProductionYear()
      this.setYachtManufacturer()

      if (!this.yachtDirty.currency) this.yachtDirty.currency = this.selectedYachtModel.currency
    },
    yachtManufacturerId: function () {
      this.fetchYachtModels()
    },
    deliveryYear: function () {
      this.fetchYachtModels()
    },
    modalVisible: function (value) {
      if (!value) return

      this.setup()
    },
    'yachtDirty.currency': function (value) {
      if (!this.yacht.id) return

      if (value !== this.yacht.currency) this.hasChangedCurrencyOrProductionYear = true
    },
    'yachtDirty.production_year': function (value) {
      if (!this.yacht.id) return

      if (value > this.yacht.production_year) this.hasChangedCurrencyOrProductionYear = true
    }
  },
  created() {
    this.currentYear = (new Date()).getFullYear()

    this.fetchOwners()
    this.fetchYachtModels()

    this.selectedEquipmentPacksDirty = sortBy(cloneDeep(this.yachtDirty.equipment_packs), 'category.order')

    this.yachtStatusInUse = Yacht.STATUS_IN_USE
    this.yachtStatusSold = Yacht.INVESTMENT_STATUS_SOLD
  },
  methods: {
    setup() {
      this.yachtDirty = cloneDeep(this.yacht)
      this.selectedEquipmentPacksDirty = sortBy(cloneDeep(this.yachtDirty.equipment_packs), 'category.order')

      this.hasReconfigured = false

      // preselect model and country, but not if it's an existing yacht
      if (!this.modalVisible || this.yacht.id) return

      this.yachtDirty.country_id = this.countryId
      this.yachtDirty.yacht_model_id = this.yachtModelId

      if (!this.yachtModelId && this.preselectYachtManufacturerId) this.yachtManufacturerId = this.preselectYachtManufacturerId

      if (this.salesSlot) {
        this.yachtDirty.ex_factory_date = this.salesSlot.ex_factory_date
        this.yachtDirty.hull_number = this.salesSlot.yard_id
      }

      setTimeout(() => {
        this.configureDialog = true
      }, 500)
    },
    async submit() {
      let validation = await this.$validator.validate()

      if (!validation) return

      this.saveYacht()
    },
    async saveYacht() {
      this.submitting = true
      try {
        let formData = {
          country_id: this.yachtDirty.country_id,
          destination_base_id: this.yacht.id && this.yacht.status === this.yachtStatusInUse ? undefined : this.yachtDirty.destination_base_id,
          current_base_id: this.yacht.id && this.yacht.status === this.yachtStatusInUse ? this.yachtDirty.current_base_id : undefined,
          ex_factory_date: this.yachtDirty.ex_factory_date ? this.$moment(this.yachtDirty.ex_factory_date).format('YYYY-MM-DD') : null,
          delivery_year: this.yachtDirty.delivery_year,
          production_year: this.yachtDirty.production_year,
          flag_country_id: this.yachtDirty.flag_country_id,
          registration_port: this.yachtDirty.registration_port,
          yacht_model_id: this.yachtDirty.yacht_model_id,
          owner_id: this.yachtDirty.owner_id,
          hull_number: this.yachtDirty.hull_number,
          name: this.yachtDirty.name,
          currency: this.yachtDirty.currency,
          engine_serial_numbers: this.yachtDirty.engine_serial_numbers,
          equipment_packs: this.hasReconfigured ? this.selectedEquipmentPacksDirty : [],
          has_reconfigured: this.hasReconfigured,
          is_already_ordered: !this.yachtDirty.id ? this.yachtDirty.is_already_ordered : undefined,
          partners_visible: this.yachtDirty.partners_visible,
          yacht_sales_slot_id: this.salesSlot ? this.salesSlot.id : undefined
        }

        let savedYacht = await Yacht.api.save(formData, this.yachtDirty.id)

        this.$store.dispatch('alert', {
          'text': 'Yacht Saved',
          'color': 'success'
        })
        this.$emit('update:yacht', savedYacht)
        this.modalVisible = false
      } catch (e) {
        if (e.response && e.response.status === 422) {
          this.$store.dispatch('alert', {
            'text': e.response.data.message,
            'color': 'warning'
          })
        } else {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
        }

        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteYacht() {
      this.deleting = true

      try {
        await Yacht.api.destroy(this.yachtDirty.id)

        this.$store.dispatch('alert', {
          'text': 'Yacht deleted',
          'color': 'info'
        })
        this.modalVisible = false

        this.$router.push({ name: 'yachts' })
      } catch (e) {
        if (e.response && e.response.status === 403) {
          this.$store.dispatch('alert', {
            'text': 'You are not allowed to delete this yacht. Either insufficient permissions or yacht is not in draft status.',
            'color': 'error'
          })
        } else {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong.',
            'color': 'error'
          })
        }

        throw e
      } finally {
        this.deleteConfirmationDialogVisible = false
        this.deleting = false
      }
    },
    fetchYachtModels: debounce(async function () {
      const query = {
        country_id: this.yachtDirty.country_id ? this.yachtDirty.country_id : undefined,
        yacht_manufacturer_id: this.yachtManufacturerId ? this.yachtManufacturerId : undefined,
        year: this.deliveryYear ? this.deliveryYear : undefined,
        status: YachtModel.STATUS_ACTIVE,
        order_by: 'label',
        is_sales_active: true
      }

      try {
        this.yachtModels.data = await YachtModel.api.fetch(query)

        if (this.yacht.id) {
          // this forces current model to be added so other things work
          this.yachtModels.data.push(this.yacht.model)
        }
      } catch (e) {
        throw e
      } finally {
        this.yachtModels.loading = false
      }
    }, 500),
    async fetchOwners() {
      if (!this.$can('view', 'Owner')) return

      this.owners.loading = true

      try {
        this.owners.data = await OwnersApi.getOwners()
      } catch (e) {
        throw e
      } finally {
        this.owners.loading = false
      }
    },
    setYachtEngineSerialNumbersArray() {
      if (!this.selectedYachtModel || !this.selectedYachtModel.engines || !this.selectedYachtModel.engines.length) {
        this.yachtDirty.engine_serial_numbers = []
        return
      }

      // initialize engine serial numbers array
      this.yachtDirty.engine_serial_numbers = this.selectedYachtModel.engines.map(engine => {
        return {
          yacht_engine_id: engine.id,
          serial_number: null
        }
      })
    },
    ownerCreated(owner) {
      this.owners.data.unshift(owner)
      this.yachtDirty.owner_id = owner.id
    },
    setProductionYear: function () {
      if (!!this.selectedYachtModel && !this.yachtDirty.production_year) {
        this.yachtDirty.production_year = Number(this.selectedYachtModel.model_year)
      }
    },
    setYachtManufacturer: function () {
      if (this.selectedYachtModel && this.selectedYachtModel.yacht_manufacturer_id !== this.yachtManufacturerId) {
        this.yachtManufacturerId = this.selectedYachtModel.yacht_manufacturer_id
      }
    },
    configurePacks: function ({ yachtModelId, countryId, equipmentPacks, currency, productionYear }) {
      if (yachtModelId !== this.selectedYachtModel.id || this.yachtDirty.country_id !== countryId) return

      this.selectedEquipmentPacksDirty = equipmentPacks
      if (currency) this.yachtDirty.currency = currency
      if (productionYear) this.yachtDirty.production_year = productionYear

      this.hasReconfigured = true
    }
  }
}
</script>

<style lang="stylus">
.v-autocomplete__content--equipment-list .v-list__tile
  height: 70px

.equipment-items--custom
  cursor: pointer

  &:hover
    background-color: rgba(0, 0, 0, .02)
</style>
